import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  IconBook2,
  IconSchool,
  IconMoodKid,
  IconCalendar,
} from '@tabler/icons-react';

import Button from '@/components/Button/Button';
import { SchoolDetailsSkeleton } from '@/components/SkeletonLoader/SchoolDetailsSkeleton';
import { SchoolStatusConfig } from '@/constants/constant';
import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useFormatText } from '@/hooks/useFormatText';
import { getSchoolDetails } from '@/service/schoolService';
import { ISchoolDetails } from '@/types';

interface ITeachersSummary {
  total_teachers?: number;
  active_teachers?: number;
  in_active_teachers?: number;
}

interface IStudentsSummary {
  total_students?: number;
  avg_engagement_rate?: string;
  top_class?: string;
}

type TSummaryCardProps = {
  label: string;
  value: string | number;
  bg: string;
  text: string;
  sub: string;
};

interface ISchoolDetailsModalProps {
  schoolId: string | undefined;
}

const SummaryCard = ({ label, value, bg, text, sub }: TSummaryCardProps) => (
  <div
    className={`py-2.5 px-4 rounded-lg flex flex-col justify-center items-center ${bg}`}
  >
    <span className={`text-xs font-semibold leading-5 ${sub}`}>{label}</span>
    <span className={`text-2xl font-semibold mt-1 ${text}`}>{value}</span>
  </div>
);

export const SchoolDetailsContent = ({
  schoolId,
}: ISchoolDetailsModalProps) => {
  const { hasAccess } = useAccess();

  const { formatDate } = useDateFormatter();
  const navigate = useNavigate();
  const [school, setSchool] = useState<ISchoolDetails | null>(null);
  const teachersSummary: ITeachersSummary = school?.teachers_summary || {};
  const studentsSummary: IStudentsSummary = school?.students_summary || {};
  const [isLoading, setIsLoading] = useState(false);
  const { formatText } = useFormatText();

  useEffect(() => {
    const fetchSchoolDetails = async () => {
      if (!schoolId) return;
      setIsLoading(true);
      try {
        const response = await getSchoolDetails(schoolId);
        if (response?.status) {
          const data = response?.data;
          setSchool(data ?? null);
        } else {
          setSchool(null);
        }
      } catch {
        setSchool(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSchoolDetails();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = [
    {
      label: formatText('school.summary-cards.total-teacher'),
      value: teachersSummary?.total_teachers ?? 0,
      icon: IconBook2,
    },
    {
      label: formatText('school.summary-cards.total-student'),
      value: school?.total_students ?? 0,
      icon: IconMoodKid,
    },

    {
      label: formatText('school.summary-cards.created-date'),
      value: school?.created_at ? formatDate(school.created_at) : '-',
      icon: IconCalendar,
    },
  ];

  const handleAllTeachersClick = () => {
    navigate(`/user-management/school/${schoolId}/teacher`, {
      state: { school_name: school?.name },
    });
  };

  const handleAllStudentsClick = () => {
    navigate(`/user-management/school/${schoolId}/student`, {
      state: { school_name: school?.name },
    });
  };

  return !isLoading ? (
    <div className="w-full bg-white space-y-10">
      {/* school header */}
      <div className="bg-brand-light-50 rounded-2xl p-6 space-y-6">
        <div className="flex gap-2 items-center">
          <div className="flex-1 flex items-center justify-between">
            <div className="flex gap-2 items-center">
              <IconSchool
                size={32}
                className=" text-white p-2 rounded-lg bg-brand-default"
              />

              <h3 className="text-lg font-semibold text-neutral-primary">
                {school?.name ?? '-'}
              </h3>
            </div>

            {(() => {
              const config =
                school?.status !== undefined
                  ? SchoolStatusConfig[
                      school.status as keyof typeof SchoolStatusConfig
                    ]
                  : undefined;
              if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

              return (
                <span
                  className={`px-2 py-0.5 text-xs font-semibold rounded-full leading-6 capitalize ${
                    config?.className
                  }`}
                >
                  {school?.status !== undefined &&
                    formatText(config?.label ?? '')}
                </span>
              );
            })()}
          </div>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-neutral-secondary leading-6">
            {formatText('school.school-detail.license-key')}:
          </span>
          {school?.license_key ? (
            <span className="px-2 py-1 bg-white rounded text-neutral-primary font-semibold leading-6">
              {school.license_key}
            </span>
          ) : (
            '-'
          )}
        </div>

        <div className="flex justify-between">
          <span className="text-neutral-secondary leading-6">
            {formatText('school.school-detail.subscription-start-date')}:
          </span>
          <span className="font-semibold text-neutral-primary leading-6">
            {school?.start_date ? formatDate(school.start_date) : '-'}
          </span>
        </div>

        <div className="flex justify-between">
          <span className="text-neutral-secondary leading-6">
            {formatText('school.school-detail.subscription-expiry')}:
          </span>
          <span className="font-semibold text-neutral-primary leading-6">
            {school?.expiry_date ? formatDate(school.expiry_date) : '-'}
          </span>
        </div>
      </div>

      {/* stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-12">
        {stats.map(({ label, value, icon: Icon }) => (
          <div key={label} className="flex items-start gap-3 min-w-0">
            {/* Icon */}
            <div className="mt-0.5 text-neutral-hover shrink-0">
              <Icon size={20} />
            </div>

            {/* Text */}
            <div className="flex flex-col gap-1 min-w-0">
              <span className="text-sm text-neutral-secondary">{label}</span>
              <span className="text-base font-medium text-neutral-primary wrap-anywhere">
                {value}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* teacher summary */}
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center w-full">
          <h4 className="text-lg font-semibold text-neutral-primary">
            {formatText('school.school-detail.teacher-summary')}
          </h4>
          <Button
            variant="outline"
            label={formatText('school.school-detail.view-teachers')}
            className="text-sm py-2.5!"
            onClick={handleAllTeachersClick}
            disabled={!(teachersSummary?.total_teachers ?? 0)}
          />
        </div>

        <div className="grid grid-cols-3 gap-6">
          <SummaryCard
            label={formatText('school.summary-cards.total-teacher')}
            value={teachersSummary.total_teachers ?? 0}
            bg="bg-warning-light-100"
            text="text-warning-primary"
            sub="text-warning-secondary"
          />
          <SummaryCard
            label={formatText('school.summary-cards.active-teachers')}
            value={teachersSummary.active_teachers ?? 0}
            bg="bg-success-light-50"
            text="text-success-primary"
            sub="text-success-secondary"
          />
          <SummaryCard
            label={formatText('school.summary-cards.inactive')}
            value={teachersSummary.in_active_teachers ?? 0}
            bg="bg-neutral-light-100"
            text="text-neutral-secondary"
            sub="text-neutral-primary"
          />
        </div>
      </div>

      {/* student summary */}
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <div className="flex justify-between items-center w-full">
            <h4 className="text-lg font-semibold text-neutral-primary">
              {formatText('school.school-detail.student-summary')}
            </h4>
            <Button
              variant="outline"
              label={formatText('school.school-detail.view-students')}
              onClick={handleAllStudentsClick}
            />
          </div>

          {/* currently hiding as the data is not available. Will enable once the API is ready. */}
          {/* <Button
          variant="outline"
          label={formatText('school.school-detail.view-students')}
          className="text-sm py-2.5!"
          onClick={handleAllStudentsClick}
        ></Button> */}
        </div>

        <div className="grid grid-cols-3 gap-6">
          <SummaryCard
            label={formatText('school.summary-cards.total-student')}
            value={studentsSummary?.total_students ?? 0}
            bg="bg-warning-light-100"
            text="text-warning-primary"
            sub="text-warning-secondary"
          />
          <SummaryCard
            label={formatText('school.summary-cards.avg-engagement-rate')}
            value={studentsSummary?.avg_engagement_rate ?? '-'}
            bg="bg-success-light-50"
            text="text-success-primary"
            sub="text-success-secondary"
          />
          <SummaryCard
            label={formatText('school.summary-cards.top-class')}
            value={studentsSummary?.top_class ?? '-'}
            bg="bg-neutral-light-100"
            text="text-neutral-secondary"
            sub="text-neutral-primary"
          />
        </div>
      </div>
    </div>
  ) : (
    <SchoolDetailsSkeleton />
  );
};
